import { useCallback, useRef, useState } from 'react';
import { GetToken } from '@utils/storage';
import * as config from '@config';

// Mirrors backend response.AccessRequestView. Debounced=true means the same
// agent already requested access within the 24h debounce window and the
// admin email was suppressed; UI shows the same toast either way.
export interface AccessRequestResult {
    submitted: boolean;
    debounced: boolean;
}

interface UseRequestTransferAccess {
    submitting: boolean;
    request: () => Promise<AccessRequestResult>;
}

function useRequestTransferAccess(): UseRequestTransferAccess {
    const [submitting, setSubmitting] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    const request = useCallback(async (): Promise<AccessRequestResult> => {
        setSubmitting(true);
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        try {
            const response = await fetch(`${(config as { apiURL2: string }).apiURL2}/transfer-redirects/access-requests`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${GetToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
                signal: abortRef.current.signal,
            });
            const body = await response.json().catch(() => ({} as Record<string, unknown>));
            if (response.status === 200) {
                const b = body as { submitted?: boolean; debounced?: boolean };
                return { submitted: b.submitted ?? true, debounced: b.debounced ?? false };
            }
            const b = body as { error?: string; message?: string };
            throw new Error(b.message ?? b.error ?? `提交失败 (${response.status})`);
        } finally {
            setSubmitting(false);
        }
    }, []);

    return { submitting, request };
}

export default useRequestTransferAccess;
